type ReloadGuard = () => boolean | Promise<boolean>;
const guards = new Set<ReloadGuard>();
export function registerResourceUpdateGuard(guard: ReloadGuard): () => void {
  guards.add(guard);
  return () => guards.delete(guard);
}
export function createResourceUpdater(options: {
  currentVersion: string;
  loadVersion: () => Promise<string>;
  reload: (version: string) => void;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  prepare?: ReloadGuard;
}) {
  let checking = false;
  let reloading = false;
  async function check(assetFailed = false): Promise<void> {
    if (checking || reloading)
      return;
    checking = true;
    try {
      let version = '';
      try {
        version = await options.loadVersion();
      }
      catch {
        if (!assetFailed)
          return;
      }
      const valid = /^[a-f\d]{40}$/i.test(version);
      if (!assetFailed && (!valid || version === options.currentVersion))
        return;
      const target = valid ? version : `${options.currentVersion}-assets`;
      const key = `probfun:resource-refresh:${options.currentVersion}`;
      try {
        if (options.storage.getItem(key) === target)
          return;
      }
      catch { return; } // Without the loop guard, a failed deployment could loop.
      if (options.prepare && !await options.prepare())
        return;
      options.storage.setItem(key, target);
      reloading = true;
      options.reload(target);
    }
    catch { /* Network/storage/save failures leave the current workspace intact. */ }
    finally { checking = false; }
  }
  return { check };
}
export function installResourceUpdates(): void {
  if (!import.meta.env.PROD)
    return;
  const updater = createResourceUpdater({
    currentVersion: import.meta.env.VITE_COMMIT_SHA,
    storage: sessionStorage,
    loadVersion: async () => {
      const response = await fetch(`/build-version.json?t=${Date.now()}`, { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
      if (!response.ok)
        throw new Error('Build version unavailable');
      const value = await response.json();
      return typeof value.version === 'string' ? value.version : '';
    },
    prepare: async () => {
      for (const guard of guards) {
        if (!await guard())
          return false;
      }
      return true;
    },
    reload: (version) => {
      const url = new URL(location.href);
      url.searchParams.set('_appVersion', version);
      location.replace(url.href);
    },
  });
  const check = () => {
    if (document.visibilityState === 'visible')
      void updater.check();
  };
  window.addEventListener('focus', check);
  window.addEventListener('pageshow', check);
  document.addEventListener('visibilitychange', check);
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    void updater.check(true);
  });
  setInterval(check, 60_000);
  // Let routed editors register their save guards before the first check.
  window.addEventListener('load', check, { once: true });
  if (document.readyState === 'complete')
    setTimeout(check, 0);
}
